import { useEffect, useState } from "react";
import { PreviewButton } from "@/components/PdfPreview";
import { useNavigate } from "react-router-dom";
import { FileDown, GitBranch } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { download } from "@/lib/api";
import { fdate, money, pct } from "@/lib/fmt";
import { VersionBadge } from "@/components/Common";

export const HistoryDialog = ({ quoteId, open, onOpenChange }) => {
  const [rows, setRows] = useState([]);
  const nav = useNavigate();
  const openRow = (r) => { onOpenChange(false); nav(`/quotations/${r.id}`); };
  useEffect(() => {
    if (open && quoteId) api.get(`/quotations/${quoteId}/history`).then((r) => setRows(r.data));
  }, [open, quoteId]);
  const first = rows[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><GitBranch className="h-5 w-5 text-blue-800" />Riwayat Penawaran {first?.quote_no}</DialogTitle>
        </DialogHeader>
        <ol className="relative border-l-2 border-blue-100 ml-3 space-y-5" data-testid="history-timeline">
          {rows.map((r, i) => {
            const diff = first && first.offer_total ? ((r.offer_total - first.offer_total) / first.offer_total) * 100 : 0;
            return (
              <li key={r.id} className="ml-6 fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`history-item-${i}`}>
                <span className={`absolute -left-[9px] mt-1.5 h-4 w-4 rounded-full border-2 border-white ${r.kind === "nego" ? "bg-amber-500" : r.rev_no ? "bg-rose-500" : "bg-blue-800"}`} />
                <div className="card-premium p-4 cursor-pointer transition-colors hover:border-blue-400 hover:bg-blue-50/40" title="Klik untuk membuka versi ini" onClick={() => openRow(r)} data-testid={`history-open-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <VersionBadge q={r} />
                      <span className="font-num text-sm font-semibold">{r.display_no}</span>
                      {r.is_latest && <span className="text-[10px] font-bold uppercase bg-blue-800 text-white px-2 py-0.5 rounded">Terakhir</span>}
                    </div>
                    <span className="flex items-center"><PreviewButton icon url={`/quotations/${r.id}/pdf`} filename={r.pdf_filename || `${r.display_no.replace(/\//g, "-")}.pdf`} title={r.display_no} testId={`history-preview-${i}`} />
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); download(`/quotations/${r.id}/pdf`, {}, r.pdf_filename || `${r.display_no.replace(/\//g, "-")}.pdf`); }} data-testid={`history-pdf-${i}`}>
                      <FileDown className="h-4 w-4 mr-1" />PDF
                    </Button></span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{fdate(r.created_at)} • oleh {r.created_by_name}</p>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                    <div><p className="text-[11px] text-slate-400">Total Penawaran</p><p className="font-num font-semibold">{money(r.offer_total, r.currency)}</p></div>
                    <div><p className="text-[11px] text-slate-400">Margin Akhir</p><p className="font-num font-semibold">{pct(r.final_margin)}</p></div>
                    <div><p className="text-[11px] text-slate-400">vs Awal</p><p className={`font-num font-semibold ${diff < 0 ? "text-rose-600" : "text-emerald-600"}`}>{i === 0 ? "-" : `${diff.toFixed(2)}%`}</p></div>
                  </div>
                  {r.kind === "nego" && r.nego_remark && <p className="mt-2 text-xs bg-amber-50 text-amber-800 rounded p-2"><b>Remark Nego:</b> {r.nego_remark}</p>}
                  {r.revision_note && <p className="mt-2 text-xs bg-rose-50 text-rose-800 rounded p-2"><b>Catatan Revisi:</b> {r.revision_note}</p>}
                </div>
              </li>
            );
          })}
        </ol>
      </DialogContent>
    </Dialog>
  );
};
